import { useState, useEffect } from 'react';
import { useContext } from 'react';
import { useRef } from 'react';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import AlternativePaymentLinks from './AlternativePaymentLinks';
import AlternativePaymentLinksPPV from './AlternativePaymentLinksPPV';
import QitafBanner from './QitafBanner';
import QitafPopup from './QitafPopup';
import { TVODOverlayType } from './TVODOverlay';
import useBraze from '../../../hooks/useBraze';
import { BRAZE } from '../../../config/analytics-events';
import { toast, RewardCoin, FlexWrapper, Text, Button, FontIcon, PinHolder } from '@intigral/shared';
import { log } from '@intigral/utils';
import { tvodGenerateOTPApi, tvodVerifyOTPApi } from '../../../services/serviceWrapper/tvodServiceWrapper';
import { QitafDiscountBreakdown } from './QitafDiscountBreakdown';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { IStore } from '../../../interfaces/StoreTypes';
import { ppvGenerateOTPApi, ppvVerifyOTPApi } from '../../../services/serviceWrapper/ppvServiceWrapper';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { useToggleWatchlistItem } from '../../../hooks/watchlist/useToggleWatchlist';

interface tvodInfo {
    priceWithVat: number;
    currency: string;
    productId: string;
}

interface InputOtpPropsType {
    tvodInfo?: tvodInfo;
    setCurrentComponentType: (e: any) => void;
    content: any;
    isPPV?: boolean;
    eventData?: any;
    onClose?: () => void;
    handlePurchase: (e: any) => void;
    qitafUpdatedPrice?: number;
    qitafApplied?: boolean;
    setQitafUpdatedPrice?: (price: number) => void;
    setQitafApplied?: (applied: boolean) => void;
}

export const InputOTP = ({
    tvodInfo,
    setCurrentComponentType,
    content,
    isPPV,
    eventData,
    onClose,
    handlePurchase,
    qitafUpdatedPrice: qitafUpdatedPriceProp,
    qitafApplied: qitafAppliedProp,
    setQitafUpdatedPrice: setQitafUpdatedPriceProp,
    setQitafApplied: setQitafAppliedProp,
}: InputOtpPropsType) => {
    const otpWaitTimerRef = useRef(null);

    // Using React Query instead of Redux for configuration
    const toggleWatchlistItemMutation = useToggleWatchlistItem();
    const { config, otpWaitTime: configOtpWaitTime } = useConfigurationSections();
    const otpWaitTime = configOtpWaitTime ?? 5 * 1000;
    const username = user.getMSISDN() || user.getUsername();
    const durationInHours = 48;

    const { priceWithVat, currency, productId } = tvodInfo;

    let tvodDescriptionOneText = isPPV ? __('ppv_description_one') || '' : __('tvod_description_one') || '';
    let tvodDescriptionTwoText = isPPV ? __('ppv_description_two') || '' : __('tvod_description_two') || '';
    const [localUpdatedPrice, setLocalUpdatedPrice] = useState(priceWithVat);
    const [localQitafApplied, setLocalQitafApplied] = useState(false);

    const updatedPrice = setQitafUpdatedPriceProp ? qitafUpdatedPriceProp ?? priceWithVat : localUpdatedPrice;
    const setUpdatedPrice = setQitafUpdatedPriceProp ?? setLocalUpdatedPrice;
    const qitafApplied = setQitafAppliedProp ? qitafAppliedProp ?? false : localQitafApplied;
    const setQitafApplied = setQitafAppliedProp ?? setLocalQitafApplied;
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{username}', username);
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{priceWithVat}', Number(updatedPrice)?.toFixed(2));
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{currency}', currency);
    tvodDescriptionTwoText = tvodDescriptionTwoText.replace('{durationInHours}', durationInHours.toString());
    let tvodButtonText = __('tvod_pay_button') || '';
    tvodButtonText = tvodButtonText.replace('{priceWithVat}', priceWithVat.toString());
    tvodButtonText = tvodButtonText.replace('{currency}', currency);
    tvodButtonText = tvodButtonText.replace('{durationInHours}', durationInHours.toString());

    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [apiProcessing, setApiProcessing] = useState(false);
    const [generateOTPButton, setGenerateOTPButton] = useState(true);

    const { track } = useContext(MixpanelContext);
    const { logBrazeCustomEvent } = useBraze();

    const [showQitafPopup, setShowQitafPopup] = useState(false);
    const [pin, setPin] = useState('');
    const PIN_LENGTH = 4;
    const isVerifyingRef = useRef(false);
    const onPaySelected = async (otp?: string) => {
        if (isVerifyingRef.current) return;
        isVerifyingRef.current = true;
        setButtonDisabled(true);
        setApiProcessing(true);
        const userInputOTP = otp ?? pin;
        if (isPPV) {
            track(EVENT.PPV.PPV_PURCHASED_STARTED, {
                'Event Title': eventData?.name,
                'Event ID': eventData?.eventId,
                'Purchase Method': 'DCB',
            });
            try {
                const verifyPpvOpt: any = await ppvVerifyOTPApi({
                    otp: userInputOTP,
                    eventData: eventData,
                    handlePurchase,
                });
                if (verifyPpvOpt?.data?.status === 'accepted') {
                    onClose();
                    track(EVENT.PPV.PPV_PURCHASED_COMPLETED, {
                        'Event Title': eventData?.name,
                        'Event ID': eventData?.eventId,
                        'Purchase Method': 'DCB',
                        Platform: 'Web',
                    });
                    toast(__('ppv_purchase_success'), 'default', {
                        notDismissible: true,
                        icon: <FontIcon name="check-circle" size="large" md="large" color="green" />,
                    });
                } else {
                    setButtonDisabled(false);
                    isVerifyingRef.current = false;
                }
            } catch (error) {
                setButtonDisabled(false);
                isVerifyingRef.current = false;
                track(EVENT.PPV.PPV_PURCHASED_FAILED, {
                    'Failure Reason': JSON.stringify(error),
                });
            }
        } else {
            try {
                track(`TVOD - Purchase - Attempt`, {});
                const verifyOpt = await tvodVerifyOTPApi({ otp: userInputOTP, productId }, handlePurchase);
                if (verifyOpt) {
                    setCurrentComponentType(TVODOverlayType.SUCCESS);
                    setTimeout(() => {
                        if (content?.Id) {
                            toggleWatchlistItemMutation.mutate({
                                itemId: content.Id,
                                category: (content?.category?.toLowerCase() ?? '') as 'linear' | 'vod',
                                isAdded: false,
                            });
                        }
                    }, 200);
                    track(`TVOD - Purchase - Success`, getItemProperties(content));
                    logBrazeCustomEvent(BRAZE.EVENTS.TVOD_PURCHASE, {
                        [BRAZE.PROPERTIES.MOVIE_NAME]: content?.textEn ?? '',
                        [BRAZE.PROPERTIES.GENRE]: content?.genre ?? '',
                        [BRAZE.PROPERTIES.LANGUAGE]:
                            (typeof content?.language === 'string' ? content.language : content.language.join(',')) ??
                            '',
                        [BRAZE.PROPERTIES.PRICE]: priceWithVat ?? '',
                    });
                } else {
                    setButtonDisabled(false);
                    isVerifyingRef.current = false;
                }
            } catch (e) {
                setButtonDisabled(false);
                isVerifyingRef.current = false;
                track(`TVOD - Purchase - Failed`, {
                    ...getItemProperties(content),
                    'Failure Reason': JSON.stringify(e),
                });
            }
        }
        setApiProcessing(false);
    };

    const getGenerateOTP = async () => {
        setGenerateOTPButton(false);
        if (isPPV) {
            await ppvGenerateOTPApi({ eventData });
        } else {
            await tvodGenerateOTPApi({ productId });
        }
        otpWaitTimerRef.current = setTimeout(() => {
            setGenerateOTPButton(true);
        }, otpWaitTime);
    };

    const getReGenerateOTP = async () => {
        setGenerateOTPButton(false);
        if (isPPV) {
            await ppvGenerateOTPApi({ eventData });
        } else {
            await tvodGenerateOTPApi({ productId });
        }
        toast(__(`otp_resent_successfully`));
        otpWaitTimerRef.current = setTimeout(() => {
            setGenerateOTPButton(true);
        }, otpWaitTime);
    };

    useEffect(() => {
        if (updatedPrice !== priceWithVat) {
            getGenerateOTP();
        }
    }, [updatedPrice]);

    const FlexWidth = withPresetWidth(FlexWrapper);

    return (
        <FlexWrapper flexDirection="column" gap={{ default: '2x', md: '3x' }}>
            <FontIcon name="cart" size="large" md="size60" color="green" />
            <Text as="h2" variant="h2" margin="0">
                {isPPV ? __('tvod_ppv_rent_now') : __('tvod_rent_now')}
            </Text>

            {qitafApplied && updatedPrice !== priceWithVat ? (
                <QitafDiscountBreakdown priceWithVat={priceWithVat} updatedPrice={updatedPrice} />
            ) : null}

            {config?.qitaf?.QitafEnabled &&
            updatedPrice === priceWithVat &&
            !qitafApplied &&
            (!isPPV || ((config as any)?.qitaf?.ppvQitafEnabled && isPPV)) ? (
                <QitafBanner
                    // @ts-ignore
                    productId={productId}
                    updatedPrice={updatedPrice}
                    setUpdatedPrice={setUpdatedPrice}
                    qitafApplied={qitafApplied}
                    setQitafApplied={setQitafApplied}
                    setShowQitafPopup={setShowQitafPopup}
                />
            ) : null}
            {showQitafPopup && (
                <QitafPopup
                    productId={productId}
                    originalPrice={priceWithVat}
                    updatedPrice={updatedPrice}
                    setCurrentComponentType={setCurrentComponentType}
                    setQitafApplied={setQitafApplied}
                    setUpdatedPrice={setUpdatedPrice}
                    setShowQitafPopup={setShowQitafPopup}
                    resetQitafProperties={qitafApplied ? true : false}
                    content={content}
                />
            )}

            <Text as="p" margin="0" color="gray" size="14">
                <span dangerouslySetInnerHTML={{ __html: tvodDescriptionOneText }} />
            </Text>
            <Text as="p" margin="0" color="gray" size="14">
                <span dangerouslySetInnerHTML={{ __html: tvodDescriptionTwoText }} />
            </Text>
            <div>
                <Text as="h4" variant="h4" margin="0" color="white">
                    {__('tvod_enter_otp_code')}
                </Text>
                <PinHolder
                    value={pin}
                    renderInput={(props) => <input {...props} />}
                    onChange={(updatePin) => {
                        setPin(updatePin);
                        if (updatePin.length === PIN_LENGTH) {
                            onPaySelected(updatePin);
                        }
                    }}
                    skipDefaultStyles
                />
            </div>
            <Text as="p" margin="0" color="brand">
                <a
                    onClick={getReGenerateOTP}
                    className={!generateOTPButton ? 'disabled' : ''}
                    // disabled={!generateOTPButton}
                >
                    {__('tvod_resend_otp')}
                </a>
            </Text>
            <FlexWidth flexDirection="column" alignItems="center" presetWidth="medium" paddingTop="2x">
                <Button
                    onClick={onPaySelected as any}
                    disabled={pin.length !== PIN_LENGTH}
                    {...(apiProcessing && { loading: true })}
                >
                    {tvodButtonText} {qitafApplied ? Number(updatedPrice).toFixed(2) : ''}{' '}
                    {qitafApplied ? __('qitaf_currency') : ''}
                </Button>
            </FlexWidth>
            {isPPV ? (
                <AlternativePaymentLinksPPV
                    onSelected={setCurrentComponentType}
                    currentOverlayType={TVODOverlayType.OTP}
                />
            ) : (
                <AlternativePaymentLinks
                    onSelected={setCurrentComponentType}
                    currentOverlayType={TVODOverlayType.OTP}
                />
            )}
        </FlexWrapper>
    );
};

// export default InputOTP;
